import { DatePipe, formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import { ColumnDataType, ColumnDefinition } from '../../model/column-definition';
import ListViewColumn from '../../model/list-view-column';
import { LocalDate } from '../../model/local-date';
import { Money, MoneyOptional } from '../../model/money';
import { BooleanTextPipe } from '../../pipe/boolean-text.pipe';
import { CurrencyPipe } from '../../pipe/currency.pipe';
import { LanguageService } from '../language.service';
import { environment } from '../../../../environments/environment';

function compare(v1: Date | number, v2: Date | number) {
  if (v1 < v2) {
    return -1;
  } else if (v1 > v2) {
    return 1;
  }
  return 0;
}

function compareMoney(v1: Money, v2: Money) {
  if (v1 && !v2) {
    return 1;
  }
  if (v2 && !v1) {
    return -1;
  }
  if (v1.amount < v2.amount) {
    return -1;
  } else if (v1.amount > v2.amount) {
    return 1;
  }
  return 0;
}

@Injectable()
export class ListViewColumnFactory {
  constructor(
    private readonly _booleanText: BooleanTextPipe,
    private readonly _currencyPipe: CurrencyPipe,
    private readonly _datePipe: DatePipe,
    private readonly _languageService: LanguageService
  ) {}

  /** Tracks already-warned (column/key-type) combinations so we warn once, not once per row. */
  private readonly _warnedTranslationKeys = new Set<string>();

  /**
   * Crash-proof translation for translated columns.
   *
   * ngx-translate throws when handed a non-string key (e.g. a number or boolean that
   * arrives because a column was mistyped). Previously such values were silently coerced
   * and the raw value rendered; we preserve that "render raw value" fallback here, but ALSO
   * emit a one-time console.warn (in dev) so the mistyped column can be found and fixed.
   */
  private _translateCellValue(value: unknown, binding: string): string {
    // null/undefined render as empty string (matches prior behaviour).
    if (value === null || value === undefined) {
      return '';
    }

    // Only non-empty strings are valid i18n keys; anything else must not reach ngx-translate.
    if (typeof value !== 'string' || value.length === 0) {
      this._warnNonStringTranslationKeyOnce(value, binding);
      return String(value);
    }

    return this._languageService.instant(value) as string;
  }

  private _warnNonStringTranslationKeyOnce(value: unknown, binding: string): void {
    if (environment.production) {
      return; // stay silent in production
    }

    const warnKey = `${binding}|${typeof value}`;
    if (this._warnedTranslationKeys.has(warnKey)) {
      return; // already warned for this column/key-type combination
    }
    this._warnedTranslationKeys.add(warnKey);

    console.warn(`translated column received non-string key (${typeof value}): ${String(value)} — column ${binding}`);
  }

  // TODO: why is there no case for ColumnDataType.decimal? Does it not need any config? --Nick
  private _decorate<TSource, TValue>(column: ListViewColumn<TSource>, definition: ColumnDefinition<TSource, TValue>) {
    switch (definition.dataType) {
      case ColumnDataType.component:
        column.viewType = definition.component;
        column.comparisonTarget = 'Original';
        break;
      case ColumnDataType.date:
        column.converter = value => {
          let d = LocalDate.fromDate(value).toDate();
          return this._datePipe.transform(d, 'shortDate', null, column.locale);
        };
        column.comparer = (d1: Date, d2: Date) => {
          if (!d1) {
            d1 = new Date(0, 0, 0);
          } else {
            d1 = LocalDate.fromDate(d1).toDate();
          }
          if (!d2) {
            d2 = new Date(0, 0, 0);
          } else {
            d2 = LocalDate.fromDate(d2).toDate();
          }

          return compare(d1, d2);
        };
        column.comparisonTarget = 'Original';
        break;
      case ColumnDataType.dateTime:
        column.converter = value => {
          let d = value;
          if (value instanceof LocalDate) {
            d = value.toDate();
          }
          return this._datePipe.transform(d, 'short', null, column.locale);
        };
        break;
      case ColumnDataType.money:
        column.converter = value => {
          return this._currencyPipe.transform(value ? value.amount : null, false, null, value ? value.currencyCode : null);
        };
        column.comparer = compareMoney;
        column.comparisonTarget = 'Original';
        break;
      case ColumnDataType.moneyOptional:
        column.converter = value => {
          if (value instanceof MoneyOptional)
            return value?.hasValue ? this._currencyPipe.transform(value.amount, false, null, value.currencyCode) : '-';
          if (value instanceof Money)
            return this._currencyPipe.transform(value ? value.amount : null, false, null, value ? value.currencyCode : null);
        };
        column.comparer = compareMoney;
        column.comparisonTarget = 'Original';
        break;
      case ColumnDataType.number:
        column.comparer = compare;
        column.converter = value => formatNumber(value, column.locale);
        column.comparisonTarget = 'Original';
        break;
      case ColumnDataType.boolean:
        column.dataType = ColumnDataType.boolean;
        column.converter = value => this._languageService.instant(this._booleanText.transform(value));
        break;
      case ColumnDataType.translation:
        column.converter = value => this._translateCellValue(value, column.binding);
        break;
      case ColumnDataType.truncated:
        column.converter = (value: string) => {
          if (value?.length > definition.options?.maxLength) {
            return value.substring(0, Math.max(0, definition.options.maxLength - 3)) + '...';
          }
          return value;
        };
        column.comparisonTarget = 'Original';
        break;
    }
  }

  create<TSource, TValue, TValueOriginal = any>(
    definitions: ColumnDefinition<TSource, TValue>[]
  ): ListViewColumn<TSource, TValue, TValueOriginal>[] {
    return definitions.map(def => {
      const col = new ListViewColumn<TSource, TValue, TValueOriginal>(
        def.binding,
        def.caption,
        def.visibility,
        def.comparer as (value1: TValue | TValueOriginal, value2: TValue | TValueOriginal) => number,
        def.helpBuilder,
        def.helpText,
        def.allowSort,
        def.serverSideSortBinding,
        def.dataType == ColumnDataType.phone
      );
      col.dataType = def.dataType;
      col.sum = def.totalSum ?? def.dataType == ColumnDataType.money;
      col.locale = LanguageService.getCultureLang();
      this._decorate(col, def);
      return col;
    });
  }
}
